import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/branding/lab-asset`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("dhoms_token") || localStorage.getItem("token") || ""}` });

function AssetBox({ kind, label, hint, hospitalId, canEdit, aspect }) {
  const [v, setV] = useState(Date.now());
  const [missing, setMissing] = useState(false);
  const ref = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${BASE}/${kind}`, fd, { headers: authHeaders(), withCredentials: true });
      toast.success(`${label} uploaded`); setMissing(false); setV(Date.now());
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    if (ref.current) ref.current.value = "";
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${label}?`)) return;
    await axios.delete(`${BASE}/${kind}`, { headers: authHeaders(), withCredentials: true });
    setMissing(true); setV(Date.now());
  };
  return (
    <div className="space-y-2">
      <Label className="label-tiny">{label}</Label>
      <div className={`w-full ${aspect} bg-slate-50 border border-slate-200 rounded-sm grid place-items-center overflow-hidden`}>
        {missing ? <span className="text-[11px] text-slate-400">Not uploaded</span>
          : <img key={v} src={`${BASE}/${kind}?hospital_id=${hospitalId}&_=${v}`} alt={label} onError={() => setMissing(true)} className="max-h-full max-w-full object-contain" data-testid={`lab-asset-${kind}-img`} />}
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`lab-asset-${kind}-file`} />
      <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled={!canEdit} onClick={() => ref.current?.click()} className="rounded-sm" data-testid={`lab-asset-${kind}-upload`}><Upload className="w-3.5 h-3.5 mr-1" />Upload</Button>
        {!missing && <Button size="sm" variant="outline" disabled={!canEdit} onClick={remove} className="rounded-sm text-rose-700" data-testid={`lab-asset-${kind}-remove`}><Trash2 className="w-3.5 h-3.5" /></Button>}
      </div>
      <div className="text-[11px] text-slate-500">{hint}</div>
    </div>
  );
}

export function LabLetterheadSettings({ form, setForm, hospitalId, canEdit }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-4" data-testid="lab-letterhead-settings">
      <div className="label-tiny flex items-center gap-1"><FlaskConical className="w-3.5 h-3.5" /> Lab Report Letterhead</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <AssetBox kind="header" label="Header banner" hint="Full-width banner printed at the top of every lab report (wide JPG/PNG)." hospitalId={hospitalId} canEdit={canEdit} aspect="aspect-[5/1]" />
        </div>
        <AssetBox kind="signature" label="Pathologist signature" hint="Signature shown above the signatory name." hospitalId={hospitalId} canEdit={canEdit} aspect="aspect-[3/2]" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="label-tiny">Signatory name</Label>
          <Input value={form.lab_signatory_name} disabled={!canEdit} onChange={(e) => setForm({ ...form, lab_signatory_name: e.target.value })} className="mt-1 h-9" data-testid="lab-signatory-name" />
        </div>
        <div>
          <Label className="label-tiny">Qualification / Reg. No.</Label>
          <Input value={form.lab_signatory_qual} disabled={!canEdit} onChange={(e) => setForm({ ...form, lab_signatory_qual: e.target.value })} className="mt-1 h-9" data-testid="lab-signatory-qual" />
        </div>
      </div>
      <div className="text-[11px] text-slate-500">Name and qualification are saved with “Save Branding”.</div>
      <div className="border-t pt-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!form.lab_preprinted} disabled={!canEdit} onChange={(e) => setForm({ ...form, lab_preprinted: e.target.checked })} data-testid="lab-preprinted-toggle" />
          Printing on pre-printed letterhead paper (hide header when printing)
        </label>
        <div>
          <Label className="label-tiny">Top blank space (mm)</Label>
          <Input type="number" min={0} max={120} value={form.lab_top_margin_mm} disabled={!canEdit || !form.lab_preprinted} onChange={(e) => setForm({ ...form, lab_top_margin_mm: Number(e.target.value) })} className="mt-1 h-9" data-testid="lab-top-margin" />
        </div>
        <div>
          <Label className="label-tiny">Bottom blank space (mm)</Label>
          <Input type="number" min={0} max={100} value={form.lab_bottom_margin_mm} disabled={!canEdit || !form.lab_preprinted} onChange={(e) => setForm({ ...form, lab_bottom_margin_mm: Number(e.target.value) })} className="mt-1 h-9" data-testid="lab-bottom-margin" />
        </div>
      </div>
    </div>
  );
}
